import React from 'react';
import styled from 'styled-components';
import { HyperLink } from 'styles/Styles.styled';

const GeneralInfoTab: React.FC = () => {
  return (
    <Container>
      <Section>
        <SectionTitle>Third-party verification of EPDs</SectionTitle>
        <p>
          All Environmental Product Declaration (EPD) published in the International EPD System must be verified by an
          approved third-party verifier (approved <strong>Individual Verifier</strong> or an 
          <strong>Accredited Certification Body</strong>).
        </p>
        <p>
          Depending on the required verification procedure, either an Individual Verifier or an Accredited Certification Body
          can carry out the assessment.
        </p>
        <ul>
          <li>
            <strong>Individual EPD Verification</strong>: Full verification of specific EPD(s) or simplified EPD verification
            for EPDs developed using a pre-verified LCA or EPD tool.
            <br />
            <strong>Can be conducted by</strong>: An approved Individual Verifier or an Accredited Certification Body.
          </li>
          <li>
            <strong>EPD Process Certification</strong>: Assessment and certification of an organisation’s internal EPD
            development process.
            <br />
            <strong>Can be conducted by</strong>: An Accredited Certification Body holding ISO 17065 accreditation (or
            equivalent).
          </li>
        </ul>
        <p>
          Read more about third-party verification{' '}
          <HyperLink href="https://www.environdec.com/services/third-party-verification" target="_blank" rel="noreferrer">
            here
          </HyperLink>
          .
        </p>
      </Section>

      <SectionWithTopMargin>
        <SectionTitle>Contacting a verifier - What to include in your initial email</SectionTitle>
        <p>
          When contacting a verifier for the first time, providing clear and structured information will help facilitate an
          efficient review and quotation process. We recommend including:
        </p>
        <ul>
          <li>Company name and contact details.</li>
          <li>Product name(s), product variations and a short product description.</li>
          <li>
            Number of EPDs to be verified, number or LCA reports and type of verification (e.g., new EPD or an update).
          </li>
          <li>Applicable or intended PCR/c-PCR (including registration number and version) and standards.</li>
          <li>Tools and/or main databases used for the LCA.</li>
          <li>Any language requirements/requests.</li>
          <li>
            Status of the LCA (e.g., planned, ongoing, completed). We recommend contacting the verifier early in the LCA and
            EPD development process.
          </li>
          <li>Planned publication timeline and proposed timeline for verification.</li>
          <li>
            Type of EPD (e.g., EPD of single product, multiple products, sector EPD, Trader EPD, product not yet/recently on
            the market).
          </li>
          <li>Any requests for Process Certification (can only be assessed by an Accredited Certification Body).</li>
        </ul>
      </SectionWithTopMargin>
    </Container>
  );
};

const Container = styled.div`
  padding: 16px 0;
`;

const Section = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.primaryWhite};
  padding: 24px 30px;
  border-radius: 4px;
  font-family: 'Inter';

  p {
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.colors.primaryBlack70};
  }

  ul {
    padding-left: 24px;
    margin-bottom: 12px;

    li {
      margin-bottom: 8px;
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme.colors.primaryBlack70};
    }
  }

  strong {
    font-weight: 600;
  }
`;

const SectionWithTopMargin = styled(Section)`
  margin-top: 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Inter';
  font-size: 16px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.primaryBlack};
`;

export default GeneralInfoTab;
